import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { UserInvitationChannelType } from '@enums/user-invitation-channel-type.enum';
import { Prospect } from '@gi/models/prospect';
import { PhoneHelper } from '@helpers/phone.helper';
import { ValidatorHelper } from '@helpers/validators.helper';
import { BusinessUnit } from '@models/business-unit';
import { Company } from '@models/company';
import { CreateCompany } from '@models/create-company';
import { User } from '@models/user';

import { CompanyForm } from './company-form';
import { UserForm } from './user/user-form';
import { UserInvitationConfigurationForm } from './user-invitation-configuration-form';

export function parametersStepValidator(): ValidatorFn {
  return (parametersStepForm: ParametersStepForm): ValidationErrors => {
    const fecFile = parametersStepForm.fecFile;
    const labelPlan = parametersStepForm.labelPlan;

    if ((labelPlan.value && !fecFile.value) || (!labelPlan.value && fecFile.value)) {
      return null;
    } else {
      return { parametersFormInvalid: 'parametersFormInvalid' };
    }
  };
}

export type CompanyStepFormType = {
  company: CompanyForm;
  companyDoesntExist: FormControl<boolean>;
};

export class CompanyStepForm extends FormGroup<CompanyStepFormType> {
  get company(): CompanyForm {
    return this.controls.company;
  }

  get companyDoesntExist(): FormControl<boolean> {
    return this.controls.companyDoesntExist;
  }

  constructor() {
    super({
      company: new CompanyForm(),
      companyDoesntExist: new FormControl<boolean>(false, Validators.requiredTrue)
    });
  }
}

export type UserStepFormType = {
  user: UserForm;
  userDoesntExist: FormControl<boolean>;
};

export class UserStepForm extends FormGroup<UserStepFormType> {
  get user(): UserForm {
    return this.controls.user;
  }

  get userDoesntExist(): FormControl<boolean> {
    return this.controls.userDoesntExist;
  }

  constructor() {
    super({
      user: new UserForm(
        [Validators.required, ValidatorHelper.emailValidator()],
        [Validators.required, PhoneHelper.phoneNumberValidator()]
      ),
      userDoesntExist: new FormControl<boolean>(false, Validators.requiredTrue)
    });
  }
}

export type InvitationStepFormType = {
  invitation: UserInvitationConfigurationForm;
};

export class InvitationStepForm extends FormGroup<InvitationStepFormType> {
  get invitation(): UserInvitationConfigurationForm {
    return this.controls.invitation;
  }

  constructor() {
    super({
      invitation: new UserInvitationConfigurationForm(false)
    });
  }
}

export type ParametersStepLabelModeType = 'labelPlan' | 'fecFile' | null;

export type ParametersStepFormType = {
  labelMode: FormControl<ParametersStepLabelModeType>;
  fecFile: FormControl<File | undefined>;
  labelPlan: FormControl<number | undefined>;
  categorySetupPlan: FormControl<number | null>;
};

export class ParametersStepForm extends FormGroup<ParametersStepFormType> {
  get labelMode(): FormControl<ParametersStepLabelModeType> {
    return this.controls.labelMode;
  }

  get fecFile(): FormControl<File | undefined> {
    return this.controls.fecFile;
  }

  get labelPlan(): FormControl<number | undefined> {
    return this.controls.labelPlan;
  }

  get categorySetupPlan(): FormControl<number | null> {
    return this.controls.categorySetupPlan;
  }

  constructor() {
    super({
      labelMode: new FormControl<ParametersStepLabelModeType>(null),
      fecFile: new FormControl<File | undefined>(undefined),
      labelPlan: new FormControl<number | undefined>(undefined),
      categorySetupPlan: new FormControl<number | undefined>(null)
    });
  }
}

export type CreateCompanyFormType = {
  companyStep: CompanyStepForm;
  userStep: UserStepForm;
  invitationStep: InvitationStepForm;
  parametersStep: ParametersStepForm;
};

export class CreateCompanyForm extends FormGroup<CreateCompanyFormType> {
  get companyStep(): CompanyStepForm {
    return this.controls.companyStep;
  }

  get userStep(): UserStepForm {
    return this.controls.userStep;
  }

  get invitationStep(): InvitationStepForm {
    return this.controls.invitationStep;
  }

  get parametersStep(): ParametersStepForm {
    return this.controls.parametersStep;
  }

  constructor() {
    super({
      companyStep: new CompanyStepForm(),
      userStep: new UserStepForm(),
      invitationStep: new InvitationStepForm(),
      parametersStep: new ParametersStepForm()
    });
  }

  initCompanyFromBusinessUnits(businessUnits: BusinessUnit[]): void {
    if (businessUnits?.length === 1) {
      this.companyStep.company.businessUnit.setValue(businessUnits[0]);
    } else {
      this.companyStep.company.legalStructureAndSignatory.disable();
    }
  }

  initUserFromProspect(prospect: Prospect): void {
    this.userStep.user.patchValue({
      firstName: prospect.firstName,
      lastName: prospect.lastName,
      phone: prospect.phone,
      email: prospect.email,
      civility: prospect.civility
    });

    this.userStep.user.address.patchValue({
      mailingStreet: prospect.mailingStreet,
      mailingPostalCode: prospect.mailingPostalCode,
      mailingCity: prospect.mailingCity
    });
  }

  initCompanyFromProspect(prospect: Prospect, businessUnits: BusinessUnit[]): void {
    const businessUnit: BusinessUnit | undefined = businessUnits.find(
      (businessUnit: BusinessUnit) => businessUnit.id === prospect.businessUnit?.id
    );

    this.companyStep.company.patchValue({
      name: prospect.companyName,
      legalForm: prospect.companyLegalForm,
      businessUnit
    });
  }

  initInvitation(): void {
    this.invitationStep.invitation.patchValue({
      features: {
        invoiceApp: true,
        receiptApp: true
      },
      channels: [UserInvitationChannelType.email],
      withFormation: true
    });
  }

  toCreateCompany(): CreateCompany {
    const company: Company = this.companyStep.company.toCompany();
    const user: User = this.userStep.user.toUser(false);
    const createCompany: CreateCompany = new CreateCompany(company, user);

    return createCompany;
  }
}
